import "@ai-foundry/sdk/ui/foundry-sidebar-chatbot"; // Side-effect import: registers the <foundry-sidebar-chatbot> custom element.
import { AfterViewInit, Component, inject, NgZone, OnDestroy } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { ForgeIconButtonModule, ForgeBadgeModule, ForgeIconModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { getLoggerFor } from "@tylertech/logging";
import { createFetchHttpPlugin, ensureSmartPulseFramework } from "@tylertech/smart-pulse-core";
import { createDOMPolicyForgeDriverPlugin, createFrameworkMenuForgeDriverPlugin, createInlineControlsForgeDriverPlugin, } from "@tylertech/smart-pulse-forge-ui";
import { ChatSidebarApi, createChatSidebarPlugin } from "@tylertech/smart-pulse-foundry-chat-sidebar";
import { createPageGraphGenericForgeDriverPlugin, createPageGraphPlugin, } from "@tylertech/smart-pulse-page-graph";
import { createRedactionPolicyDefaultDriverPlugin } from "@tylertech/smart-pulse-redaction-policy";
import { createUserActivityPlugin } from "@tylertech/smart-pulse-user-activity";
import { distinctUntilChanged, filter, map, startWith } from "rxjs";
import { Agent, AiAgentService, FeatureFlagHelperService, FeatureFlags, createCxEscalationPlugin, CxBridgeService, FoundryChatAdapter, ReleaseStage, SecurityService, TylerAssistantSidebarService, BrowserNotificationService, BrowserNotificationKeys } from "../../../services";
import { createFollowMePlugin } from "./followMe.plugin";
import { createBrowserNotificationPlugin } from "./browserNotification.plugin";
import { publishRightPanelWidth } from "./rightPanelWidth";

const DRAWER_OPEN_KEY = "sp-drawer-open";
const THREAD_KEY = "sp-thread-id";

@Component({
    selector: "smart-pulse",
    templateUrl: "./smartPulse.tmpl.html",
    imports: [
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        ForgeBadgeModule
    ],
})
export class SmartPulseComponent implements AfterViewInit, OnDestroy
{
    private readonly $aiAgentService = inject(AiAgentService);
    private readonly $securityService = inject(SecurityService);
    private readonly $cxBridgeService = inject(CxBridgeService);
    private readonly $browserNotificationService = inject(BrowserNotificationService);
    private readonly $foundryChatAdapter = inject(FoundryChatAdapter);
    private readonly $featureFlagHelperService = inject(FeatureFlagHelperService);
    private readonly $router = inject(Router);
    private readonly $ngZone = inject(NgZone); 
    private readonly $tylerAssistantSidebarService = inject(TylerAssistantSidebarService);
    protected readonly sidebar = this.$tylerAssistantSidebarService.sidebar;

    private readonly log = getLoggerFor("SmartPulseComponent");
    private wafRetryHandle?: ReturnType<typeof setTimeout>;
    private wafRetriesRemaining = 20;
    private cxResumeFallbackHandle?: ReturnType<typeof setTimeout>;
    private usingClientBridge: boolean = false;
    private stopPublishingDrawerWidth?: () => void;
    protected readonly unreadCount = this.$browserNotificationService
        .getNotificationCount(BrowserNotificationKeys.SMARTPULSE_NOTIFICATION_KEY);

    constructor()
    {
        this.$tylerAssistantSidebarService.reconnectRequested
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.reconnect());
    }

    async ngAfterViewInit(): Promise<void>
    {
        if (this.sidebar() != null)
            return;
        performance.mark("smart-pulse:fetch-start");
        try
        {
            await this.$aiAgentService.fetchAgents();
        }
        catch (error)
        {
            this.log.error("Failed to fetch AI agents", error);
            return;
        }
        performance.measure("smart-pulse:fetch", "smart-pulse:fetch-start");
        const agent = this.$aiAgentService.currentAgent;
        if (!agent)
        {
            this.log.error("Unable to find current agent.");
            return;
        }
        this.usingClientBridge = agent.EnableClientBridge ?? false;

        const sidebarContainer = document.querySelector<HTMLElement>("forge-scaffold")
            ?? document.body;

        await this.$featureFlagHelperService.whenReady;
        performance.mark("smart-pulse:launch-start");
        this.launch(agent, sidebarContainer);
        performance.measure("smart-pulse:launch", "smart-pulse:launch-start");
    }

    toggleSidebar(): void
    {
        if (this.$tylerAssistantSidebarService.foundryUnavailable())
        {
            this.$tylerAssistantSidebarService.fallbackOpen.update(open => !open);
            return;
        }

        const sidebar = this.sidebar();
        if (!sidebar)
            return;

        if (sidebar.isOpen())
            sidebar.close();
        else
        {
            sidebar.open();
            //While we have the check as a fallback in the browser, this line is to give a pop-up as soon as they click the sidebar.
            this.$browserNotificationService.checkOrGetBrowserPermission();
            this.$browserNotificationService
                .clearNotificationCountForKey(BrowserNotificationKeys.SMARTPULSE_NOTIFICATION_KEY);
        }
    }

    private applyLocalWafWorkaround(api: ChatSidebarApi): void
    {
        if (!this.$aiAgentService.isLocal)
            return;

        const component = api.getComponent();
        if (!component)
        {
            if (this.wafRetriesRemaining-- <= 0)
                return;
            this.wafRetryHandle = setTimeout(() => this.applyLocalWafWorkaround(api), 250);
            return;
        }

        const originalSetContext = component.setContext.bind(component);
        component.setContext = (ctx: Record<string, unknown>) =>
            originalSetContext({ ...ctx, pageUrl: `${location.host}${location.pathname}` });
        component.setContext({});
    }

    private attachCxBridge(api: ChatSidebarApi): void
    {
        if (!this.usingClientBridge)
            return;

        const component = api.getComponent();
        if (!component)
        {
            this.log.error("Chat sidebar component not found - CX escalation bridge not attached.");
            return;
        }

        // The sidebar element shares the floating embed's API surface (see foundryChatbot.ts);
        // all direct element access stays contained in FoundryChatAdapter. The escalate
        // tool itself is contributed by createCxEscalationPlugin, not attached here.
        this.$foundryChatAdapter.attach(component);
    }

    private restoreDrawerOpenState(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component)
            return;

        component.open = sessionStorage.getItem(DRAWER_OPEN_KEY) === "true";
        component.addEventListener("foundry-sidebar-chatbot-open", () => sessionStorage.setItem(DRAWER_OPEN_KEY, "true"));
        component.addEventListener("foundry-sidebar-chatbot-close", () => sessionStorage.setItem(DRAWER_OPEN_KEY, "false"));
    }

    private restoreThread(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component)
            return;

        const assignThread = (threadId: string): void =>
        {
            sessionStorage.setItem(THREAD_KEY, threadId);
            component.threadId = threadId;
        };

        const setupChat = (): void =>
        {
            assignThread(crypto.randomUUID());
            if (this.usingClientBridge)
                this.$cxBridgeService.clearPreviousEscalationChats();
        };

        assignThread(sessionStorage.getItem(THREAD_KEY) ?? crypto.randomUUID());
        component.addEventListener("foundry-chatbot-new-chat", () => setupChat());
        component.addEventListener("foundry-chatbot-cleared", () => setupChat());
    }

    private restoreCXSession(api: ChatSidebarApi): void
    {
        if (!this.usingClientBridge)
            return;

        const component = api.getComponent();
        if (!component)
            return;

        //Ideally, we should have the cx session happen only after the chatbot has connected so no conflicts happens when using both.
        clearTimeout(this.cxResumeFallbackHandle);
        let resumed = false;
        const resume = (): void =>
        {
            if (resumed)
                return;
            resumed = true;

            clearTimeout(this.cxResumeFallbackHandle);
            component.removeEventListener("foundry-chatbot-state-restored", resume);

            this.$cxBridgeService.populatePreviousEsclationChats()
                .finally(() => this.$cxBridgeService.checkIfEscalationCanResume());
        };
        component.addEventListener("foundry-chatbot-state-restored", resume);
        this.cxResumeFallbackHandle = setTimeout(() =>
        {
            this.log.warning("Foundry did not report a restored thread - resuming live chat anyway.");
            resume();
        }, 5000);
    }

    private publishDrawerWidth(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component) return;
        this.stopPublishingDrawerWidth = publishRightPanelWidth(component);
    }

    private watchForFoundryOutage(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component) return;
        component.addEventListener("foundry-chatbot-error", event =>
        {
            this.log.warning("Tyler Assistant Unavailable", event.detail.error.code, event.detail.error.message);
            this.$tylerAssistantSidebarService.fallbackOpen.set(component.open);
            this.$tylerAssistantSidebarService.foundryUnavailable.set(true);
            component.style.display = "none";
        });
    }

    private addNotifications(api: ChatSidebarApi)
    {
        const component = api.getComponent();
        if (!component) return;

        component.addEventListener("foundry-chatbot-run-finished", event =>
        {
            this.$browserNotificationService.sendNotification("Tyler Assistant Done",
                "Agent has finished running."
                , !(this.sidebar()?.isOpen() ?? false),
                BrowserNotificationKeys.SMARTPULSE_NOTIFICATION_KEY);
            if (!(this.sidebar()?.isOpen() ?? false))
                this.$browserNotificationService
                    .incrementNotificationCountForKey(BrowserNotificationKeys.SMARTPULSE_NOTIFICATION_KEY);
        });
    }

    private wireSidebar(api: ChatSidebarApi): void
    {
        this.applyLocalWafWorkaround(api);
        this.attachCxBridge(api);
        this.restoreDrawerOpenState(api);
        this.restoreThread(api);
        this.publishDrawerWidth(api);
        this.watchForFoundryOutage(api);
        this.addNotifications(api);
        this.restoreCXSession(api);
    }

    /** Replaces the failed chat element with a fresh one; the Smart Pulse framework itself is a global singleton and stays up. */
    private reconnect(): void
    {
        const sidebar = this.sidebar();
        const agent = this.$aiAgentService.currentAgent;
        if (!sidebar || !agent)
            return;

        this.$tylerAssistantSidebarService.foundryUnavailable.set(false);
        this.$tylerAssistantSidebarService.fallbackOpen.set(false);
        this.$ngZone.runOutsideAngular(() =>
        {
            this.stopPublishingDrawerWidth?.();
            sidebar.dispose();
            sidebar.init();
            this.wireSidebar(sidebar);
            sidebar.open();
        });
    }

    private launch(agent: Agent, sidebarContainer: HTMLElement): void
    {
        const plugins = [
            createDOMPolicyForgeDriverPlugin({}),
            createInlineControlsForgeDriverPlugin(),
            createFrameworkMenuForgeDriverPlugin(),
            createFetchHttpPlugin(),
            createPageGraphGenericForgeDriverPlugin({ includeFieldValues: true }),
            createPageGraphPlugin({ registerAsContextProvider: true }),
            createRedactionPolicyDefaultDriverPlugin({ showLastFourMasked: true }),
            createUserActivityPlugin({ includeFieldValues: true, maxActivityEntries: 10, registerAsContextProvider: true }),
            // Must precede createChatSidebarPlugin: the sidebar collects tool
            // definitions once, in its afterInit, and afterInit hooks run in array order.
            ...(this.usingClientBridge ? [createCxEscalationPlugin(this.$cxBridgeService)] : []),
            createChatSidebarPlugin(
                {
                    baseUrl: this.$aiAgentService.foundryBaseUrl(agent.ReleaseStage),
                    chatButtonLabel: agent.Description ?? "Tyler Assistant",
                    chatButtonTooltip: "",
                    defaultAgentId: agent.AgentId,
                    sidebarContainer,
                    pickerAgents: [],
                },
                api =>
                {
                    this.$tylerAssistantSidebarService.sidebar.set(api);
                    this.wireSidebar(api);
                }),
        ];

        const followMeEnabled = this.$featureFlagHelperService.get(FeatureFlags.Shared_All_SmartPulseFollowMe)();
        const browserNotificationsEnabled = this.$featureFlagHelperService.get(FeatureFlags.Shared_All_BrowserNotifications)();

        if (followMeEnabled)
        {
            plugins.push(createFollowMePlugin(this.$foundryChatAdapter.onLiveModeChanged, followMe =>
                this.$router.events
                    .pipe(
                        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                        map(event => event.urlAfterRedirects.split(/[?#]/)[0]),
                        startWith(this.$router.url.split(/[?#]/)[0]),
                        distinctUntilChanged())
                    .subscribe(() => followMe.noteNavigation())));
        }
        if (browserNotificationsEnabled)
            plugins.push(createBrowserNotificationPlugin(
                () => this.$browserNotificationService.checkIssuesForNotifications(),
                () => this.$browserNotificationService.getCurrentPermission()));

        try
        {
            this.$ngZone.runOutsideAngular(() => ensureSmartPulseFramework({
                hostAppId: "tyl_erp_pro10", // TODO: derive per app instead of hardcoding
                hostAppName: "ERP Pro 10",  // TODO: derive per app instead of hardcoding
                userId: this.$securityService.principle?.Email ?? "",
                // Best effort: Smart Pulse only forwards env to plugin settings and logger
                env: agent.ReleaseStage === ReleaseStage.Staging ? "test" : "prod",
                settings: {},
                plugins,
            }));
        }
        catch (error)
        {
            this.log.error("Smart Pulse failed to launch", error);
        }
    }

    ngOnDestroy(): void
    {
        clearTimeout(this.wafRetryHandle);
        clearTimeout(this.cxResumeFallbackHandle);

        if (this.$securityService.isAuthenticated)
            return;

        sessionStorage.removeItem(THREAD_KEY);
        this.sidebar()?.close();
        if (this.usingClientBridge)
        {
            this.$cxBridgeService.teardown();
            this.$cxBridgeService.clearPreviousEscalationChats();
        }
    }
}
